// # Calendar 3D View Component
// 3D perspective view with depth and rotation effects

'use client';

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays, RotateCw, RotateCcw } from 'lucide-react';
import type { CalendarEvent } from '../../types/calendar';
import { CalendarEventChip } from './CalendarEventChip';
import { useDeviceInfo } from '../../hooks/useDeviceInfo';

interface Calendar3DViewProps {
  events: CalendarEvent[];
  onEventClick?: (event: CalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  onCreateEvent?: (date: Date) => void;
  className?: string;
  showWeekends?: boolean;
  firstDayOfWeek?: number;
}

export function Calendar3DView({
  events,
  onEventClick,
  onDateClick,
  onCreateEvent,
  className = '',
  showWeekends = true,
  firstDayOfWeek = 1
}: Calendar3DViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [rotationX, setRotationX] = useState(-15); // Tilt down
  const [rotationY, setRotationY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useDeviceInfo();

  // # Get days for week view
  const getDaysToDisplay = useCallback((): Date[] => {
    const days: Date[] = [];
    let startOfWeek = new Date(currentDate);
    
    while (startOfWeek.getDay() !== firstDayOfWeek) {
      startOfWeek.setDate(startOfWeek.getDate() - 1);
    }
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    
    return days;
  }, [currentDate, firstDayOfWeek]);

  // # Format date helper
  const formatDate = useCallback((date: Date): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return new Date().toISOString().split('T')[0];
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // # Get events for a specific date
  const getEventsForDate = useCallback((date: Date): CalendarEvent[] => {
    if (!events || events.length === 0) return [];
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return [];
    
    const dateStr = formatDate(date);
    
    return events.filter(event => {
      if (!event || !event.start_date || !event.end_date) return false;
      const normalizedStart = event.start_date.split('T')[0];
      const normalizedEnd = event.end_date.split('T')[0];
      return normalizedStart <= dateStr && normalizedEnd >= dateStr;
    });
  }, [events, formatDate]);

  // # Check if date is today
  const isToday = useCallback((date: Date): boolean => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return false;
    }
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }, []);

  // # Navigation handlers
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // # Rotation handlers
  const resetRotation = () => {
    setRotationX(-15);
    setRotationY(0);
  };

  const rotateLeft = () => {
    setRotationY(prev => prev - 15);
  };

  const rotateRight = () => {
    setRotationY(prev => prev + 15);
  };

  // # Mouse drag handlers for 3D rotation
  const handleMouseDown = (e: React.MouseEvent) => {
    if (isMobile) return;
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || isMobile) return;
    
    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;
    
    setRotationY(prev => prev + deltaX * 0.5);
    setRotationX(prev => Math.max(-45, Math.min(15, prev - deltaY * 0.5)));
    
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  }, [isDragging, isMobile]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // # Touch handlers for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    if (e.touches.length === 1) {
      setIsDragging(true);
      dragStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging || !isMobile || e.touches.length !== 1) return;
    
    const deltaX = e.touches[0].clientX - dragStartRef.current.x;
    const deltaY = e.touches[0].clientY - dragStartRef.current.y;
    
    setRotationY(prev => prev + deltaX * 0.5);
    setRotationX(prev => Math.max(-45, Math.min(15, prev - deltaY * 0.5)));
    
    dragStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, [isDragging, isMobile]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // # Event listeners for drag
  useEffect(() => {
    if (isDragging) {
      if (isMobile) {
        window.addEventListener('touchmove', handleTouchMove);
        window.addEventListener('touchend', handleTouchEnd);
      } else {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
      }
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, isMobile, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // # Get current period label
  const getCurrentPeriodLabel = (): string => {
    const days = getDaysToDisplay();
    const start = days[0];
    const end = days[days.length - 1];
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  // # Get weekday names
  const getWeekdayNames = (): string[] => {
    const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const reordered = [...names.slice(firstDayOfWeek), ...names.slice(0, firstDayOfWeek)];
    return showWeekends ? reordered : reordered.filter((_, i) => i < 5);
  };

  const days = getDaysToDisplay();
  const weekdayNames = getWeekdayNames();

  // # 3D transform style
  const transform3D = {
    transform: `perspective(1000px) rotateX(${rotationX}deg) rotateY(${rotationY}deg)`,
    transformStyle: 'preserve-3d' as const,
  };

  return (
    <div className={`flex flex-col h-full overflow-hidden ${className}`}>
      {/* Header with navigation and rotation controls */}
      <div className={`flex-shrink-0 border-b border-gray-100 ${isMobile ? 'px-2 py-1.5' : 'px-3 py-2'}`}>
        <div className="flex items-center justify-between mb-2">
          <h2 className={`font-bold text-gray-900 ${isMobile ? 'text-sm' : 'text-base'}`}>
            {getCurrentPeriodLabel()}
          </h2>
          <div className={`flex items-center ${isMobile ? 'gap-1' : 'gap-1.5'}`}>
            {!isMobile && (
              <button
                onClick={goToToday}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-all duration-200 flex items-center gap-1.5"
              >
                <CalendarDays className="w-3.5 h-3.5" />
                <span>Today</span>
              </button>
            )}
            <div className={`flex items-center bg-gray-100 rounded-md ${isMobile ? 'gap-0 p-0.5' : 'gap-0.5 p-0.5'}`}>
              <button
                onClick={goToPrevious}
                className={`text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors ${isMobile ? 'p-1' : 'p-1.5'}`}
                title="Previous week"
              >
                <ChevronLeft className={isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
              </button>
              <button
                onClick={goToNext}
                className={`text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors ${isMobile ? 'p-1' : 'p-1.5'}`}
                title="Next week"
              >
                <ChevronRight className={isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Rotation controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              {isMobile ? 'Drag to rotate' : 'Drag calendar to rotate'}
            </span>
          </div>
          <div className="flex items-center bg-gray-100 rounded-md gap-0.5 p-0.5">
            <button
              onClick={rotateLeft}
              className={`text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors ${isMobile ? 'p-1' : 'p-1.5'}`}
              title="Rotate left"
            >
              <RotateCcw className={isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
            </button>
            <button
              onClick={resetRotation}
              className={`text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors ${isMobile ? 'p-1' : 'p-1.5'}`}
              title="Reset rotation"
            >
              <span className={`${isMobile ? 'text-[10px]' : 'text-xs'} font-medium`}>Reset</span>
            </button>
            <button
              onClick={rotateRight}
              className={`text-gray-600 hover:text-gray-900 hover:bg-white rounded transition-colors ${isMobile ? 'p-1' : 'p-1.5'}`}
              title="Rotate right"
            >
              <RotateCw className={isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
            </button>
          </div>
        </div>
      </div>

      {/* 3D Calendar container */}
      <div 
        className="flex-1 overflow-auto"
        style={{ 
          touchAction: 'pan-y',
          userSelect: 'none'
        }}
      >
        <div 
          ref={containerRef}
          style={transform3D}
          className={`flex-1 flex flex-col ${isMobile ? 'p-4' : 'p-6'}`}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          {/* Weekday headers with 3D effect */}
          <div className={`grid grid-cols-7 flex-shrink-0 ${isMobile ? 'mb-3' : 'mb-4'}`}>
            {weekdayNames.map((day, index) => {
              // Add depth based on position
              const depth = (index - 3) * 5; // Center days closer, edges further
              return (
                <div
                  key={day}
                  style={{
                    transform: `translateZ(${depth}px)`,
                  }}
                  className={`text-center font-bold text-gray-700 uppercase ${
                    isMobile ? 'text-sm py-2' : 'text-base py-3'
                  } border-b-2 border-gray-200 bg-white rounded-t-lg shadow-sm`}
                >
                  {day}
                </div>
              );
            })}
          </div>

          {/* Calendar days with 3D depth */}
          <div className={`flex-1 grid grid-cols-7 grid-rows-1 gap-${isMobile ? '2' : '3'}`}>
            {days.map((date, index) => {
              if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
                return null;
              }
              
              const dateEvents = getEventsForDate(date);
              const isTodayDate = isToday(date);
              const isSelected = selectedDate && formatDate(selectedDate) === formatDate(date);
              
              // Add depth based on position and event count
              const depth = (index - 3) * 8; // Center days closer
              const eventDepth = Math.min(dateEvents.length * 2, 10); // Events add depth
              const totalDepth = depth + eventDepth;

              return (
                <div
                  key={`${date.toISOString()}-${index}`}
                  style={{
                    transform: `translateZ(${totalDepth}px)`,
                  }}
                  className={`border-2 rounded-lg cursor-pointer transition-all duration-200 flex flex-col ${
                    isMobile ? 'p-3' : 'p-4'
                  } ${
                    isTodayDate
                      ? 'bg-blue-50 border-blue-300 shadow-xl'
                      : isSelected
                      ? 'bg-blue-50/50 border-blue-200 shadow-lg'
                      : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-lg'
                  } min-h-[180px]`}
                  onClick={() => {
                    setSelectedDate(date);
                    onDateClick?.(date);
                  }}
                >
                  {/* Day number */}
                  <div className={`flex items-center justify-between mb-${isMobile ? '2' : '3'}`}>
                    <span
                      className={`font-bold ${
                        isMobile ? 'text-lg' : 'text-xl'
                      } ${
                        isTodayDate
                          ? `text-blue-700 bg-blue-200 rounded-full flex items-center justify-center ${
                              isMobile ? 'w-7 h-7' : 'w-8 h-8'
                            }`
                          : 'text-gray-900'
                      }`}
                    >
                      {date.getDate()}
                    </span>
                    {dateEvents.length > 0 && (
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        isTodayDate 
                          ? 'bg-blue-200 text-blue-700' 
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {dateEvents.length}
                      </span>
                    )}
                  </div>

                  {/* Events with 3D stacking effect */}
                  <div className={`flex-1 space-y-${isMobile ? '1' : '1.5'} overflow-y-auto`}>
                    {dateEvents.length === 0 ? (
                      <div className="text-center text-gray-400 text-xs py-4">
                        No events
                      </div>
                    ) : (
                      dateEvents.map((event, eventIndex) => (
                        <div
                          key={event.id}
                          style={{
                            transform: `translateZ(${eventIndex * 3}px)`,
                          }}
                        >
                          <CalendarEventChip
                            event={event}
                            onClick={(evt) => {
                              onEventClick?.(evt);
                            }}
                            compact={false}
                          />
                        </div>
                      ))
                    )}
                  </div>

                  {/* Quick add button */}
                  {onCreateEvent && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onCreateEvent(date);
                      }}
                      className={`mt-2 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded px-2 py-1 transition-colors ${
                        isMobile ? 'text-[10px]' : ''
                      }`}
                    >
                      + Add event
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Calendar3DView;

